/**
 * Pure helpers for the shared <LibraryDocumentsView scope> component
 * (Library-PR-X3 / Phase 4).
 *
 * Per project pattern (PR-B agent-new-shared, PR-E prompt-editor-shared):
 * pure logic only; tests assert on these without any DOM / RTL.
 */
import type { DocumentKind } from '@argusai/shared';
import type { NormalizedLibraryDoc } from './LibraryDocumentsView.types';

// ─── Time formatting ────────────────────────────────────────────────────────

/**
 * Relative time formatter — "Just now" / "5m ago" / "2h ago" / "3d ago" /
 * `toLocaleDateString()` for older than 30 days.
 *
 * Both pre-refactor route files had near-identical `timeAgo` helpers
 * (tenant added "Just now" with min < 1; sysadmin omitted that branch
 * but reached the same code path via the minute fallback). The unified
 * version preserves the tenant-side "Just now" affordance.
 */
export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ─── Byte formatting ────────────────────────────────────────────────────────

/**
 * Human-readable bytes — "512 B" / "12.5 KB" / "3.4 MB".
 *
 * Both pre-refactor route files had identical `formatBytes` / `formatSize`
 * helpers (tenant called it formatSize; sysadmin formatBytes). The
 * unified version uses the sysadmin name (formatBytes) since it's
 * dominant in the codebase.
 */
export function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── No filter pipeline, no counts (Library LB6-T2, L6-15) ──────────────────
//
// `filterDocs` (kind + text over the loaded rows) and `countByKind` (the
// header's per-kind tallies over the loaded rows) are DELETED. The list asks
// its source for every filter and the order, and the header renders the
// source's counts (GET /knowledge/documents `total` / `countsByKind` for the
// tenant). scripts/ci/no-web-derivation-lint.mjs (W6) fails the run if a
// client filter or sort over document fields comes back here.

// ─── Kind classification for bulk-action constraints ───────────────────────

/**
 * Returns the set of kinds present in the current selection. Used by
 * the bulk action bar to detect mixed selections (sysadmin's bulk
 * publish accepts one kind at a time).
 */
export function selectedKindsSet(
  selectedIds: Set<string>,
  filteredDocs: NormalizedLibraryDoc[],
): Set<DocumentKind> {
  const set = new Set<DocumentKind>();
  for (const d of filteredDocs) {
    if (selectedIds.has(d.id)) set.add(d.kind);
  }
  return set;
}

// ─── Selection helpers ──────────────────────────────────────────────────────

/**
 * Returns the IDs of every selectable doc in the current filtered view.
 * Used by the "Select all" header checkbox.
 *
 * `isSelectable` comes from the adapter — tenant excludes articles,
 * sysadmin allows all.
 */
export function selectableIds(
  filteredDocs: NormalizedLibraryDoc[],
  isSelectable: (d: NormalizedLibraryDoc) => boolean,
): string[] {
  return filteredDocs.filter(isSelectable).map((d) => d.id);
}

/**
 * The selection, kept to the rows ON SCREEN (LB6T2-BUG-09): a selected row the
 * list no longer shows — deleted, moved out of this folder, or left behind by
 * a new question — leaves the selection, so a bulk action only ever reaches
 * rows the person can see. Returns the SAME set when nothing changed (no
 * re-render).
 */
export function selectionOnScreen(
  selected: ReadonlySet<string>,
  onScreen: ReadonlySet<string>,
): ReadonlySet<string> {
  if (selected.size === 0) return selected;
  const kept = new Set([...selected].filter((id) => onScreen.has(id)));
  return kept.size === selected.size ? selected : kept;
}

/**
 * The Tag filter against the tag catalogue (LB6cT1-REV-04): a chosen tag that
 * is no longer in the loaded catalogue (deleted or merged, from the Tags
 * manager or elsewhere) leaves the filter — it answers 'all' — so the list is
 * never narrowed by a tag no control shows. Until the catalogue has loaded
 * (`tags` undefined) the choice stands; 'all' is always kept.
 */
export function tagFilterInCatalogue(value: string, tags: ReadonlyArray<{ id: string }> | undefined): string {
  if (value === 'all' || tags === undefined) return value;
  return tags.some((t) => t.id === value) ? value : 'all';
}
